import BugIcon from "@assets/icons/bug.svg";
import DocumentsIcon from "@assets/icons/documents.svg";
import InfoIcon from "@assets/icons/info.svg";
import LogoutIcon from "@assets/icons/logout.svg";
import MailIcon from "@assets/icons/mail.svg";
import PrivacyIcon from "@assets/icons/privacy.svg";
import HelpIcon from "@assets/icons/help.svg";
import SettingsIcon from "@assets/icons/settings.svg";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { analyticsService, userManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { useUser } from "@domain/user/hooks/use-user";
import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { RoutesPath } from "@ui/navigation/routes";
import { MenuRow } from "@ui/navigation/tab-bar/components/menu-row";
import { NotificationCenterIcon } from "@ui/navigation/tab-bar/components/notification-center-icon";
import { useCountryConfig } from "@ui/pages/patient-details/hooks/use-country-config";
import { physioMeMobileBreakpoint, row } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import { Menu } from "primereact/menu";
import { MenuItem } from "primereact/menuitem";
import React, { useContext, useMemo, useRef } from "react";

// helper for creating menu item templates outside of component definition
function menuTemplate(
	icon: React.FC<React.SVGProps<SVGElement>>,
	title: string,
	onClick: () => void,
): MenuItem["template"] {
	// eslint-disable-next-line react/display-name
	return () => <MenuRow icon={icon} title={title} onClick={onClick} />;
}

export const ProfileMenu: React.FC = () => {
	const user = useUser();
	const { countryConfig } = useCountryConfig();

	
	const { format } = useI18n();
	const profileMenuRef = useRef<Menu>(null);
	const navigate = useNavigate();
	const dialogContext = useContext(DialogContext);

	const profileMenuItems: MenuItem[] = useMemo(
		() => [
			...(user?.isTester
				? [
					{ template: menuTemplate(BugIcon, "Debug page", () => navigate(RoutesPath.Debug)) },
				]
				: []),
			{ template: menuTemplate(PrivacyIcon, format("tabbar.privacyNotice"), () => navigate(RoutesPath.PrivacyNotice)) },
			{ template: menuTemplate(DocumentsIcon, format("tabbar.termsOfUse"), () => navigate(RoutesPath.TermsOfUse)) },
			...(user?.hasAgreedToLegalDocuments
					? [{ template: menuTemplate(SettingsIcon, format("tabbar.settings"), () => navigate(RoutesPath.Settings)) }]
				: []),
			...(isNotNil(countryConfig?.faqUrl)
				? [
						{
							template: menuTemplate(
								HelpIcon,
								format("tabbar.faq"),
								() => {
									analyticsService.trackEvent({
										category: AnalyticsCategory.COMMUNICATION,
										action: "viewFAQ",
										params: [countryConfig.faqUrl],
									});
									window.open(countryConfig.faqUrl, "_blank");
								},
							),
						},
					]
				: []),
			{ template: menuTemplate(InfoIcon, format("tabbar.about"), () => {
				dialogContext?.openModal(ModalId.AboutPhysiome);
			}) },
			{ template: menuTemplate(MailIcon, format("global.contactUs"), () => {
				analyticsService.trackEvent({
					category: AnalyticsCategory.COMMUNICATION,
					action: "contactCustomerSupport",
				});
				window.open(`mailto:${countryConfig?.customerSupportEmail}`);
			}) },
			{ template: menuTemplate(LogoutIcon, format("tabbar.logout"), () => {
				userManager.logout();
				analyticsService.trackEvent({ category: AnalyticsCategory.LOGIN, action: "logout" });
				navigate(RoutesPath.Login);
			}) },
		],
		[countryConfig, dialogContext, format, navigate, user?.hasAgreedToLegalDocuments, user?.isTester],
	);

	return (
		<MenuContainer>
			<NotificationCenterIcon />
			<MenuItemContainer
				data-test={e2eTestId.homePage.profileMenu}
				className="p-size-m p-button p-button-text"
				onClick={event => profileMenuRef.current?.toggle(event)}
			>
				<StyledMenu model={profileMenuItems} popup ref={profileMenuRef} />
				<RoundProfileContainer>
					<RoundProfile className="p-size-s-rubik-medium">
						{`${user?.firstName.charAt(0)}${user?.lastName.charAt(0)}`}
					</RoundProfile>
				</RoundProfileContainer>
				<i className="black pi pi-angle-down hidden lg:block" />
			</MenuItemContainer>
		</MenuContainer>
	);
};

const RoundProfileContainer = styled.div`
	position: relative;
`;

const MenuContainer = styled.div`
	${row(0, "center", "center")};
	align-self: stretch;
`;

const MenuItemContainer = styled.button`
	${row(8, "flexstart", "center")};

	border: none;
	background: ${colors.transparent};
	cursor: pointer;

	.black {
		color: ${colors.black};
	}

	@media ${physioMeMobileBreakpoint} {
		margin: 0;
	}
`;

const RoundProfile = styled.div`
	width: 2.5rem;
	height: 2.5rem;
	${row(undefined, "center", "center")};
	border-radius: 50%;

	background-color: ${colors.primary10};
	text-transform: uppercase;
	color: ${colors.primary};
`;

const StyledMenu = styled(Menu)`
	background-color: ${colors.white} !important;
	margin-top: 1px;
	width: auto;
	max-width: 400px;
`;
